import {
  Timestamp,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import type { Query, QueryConstraint, Unsubscribe } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  AddEmergencyNoteInput,
  CancelFalseAlarmInput,
  EmergencyCommandResponse,
  EmergencyCommandOperation,
  ReassignEmergencyResponderInput,
  AssignPrimaryEmergencyResponderInput,
  EmergencyIncidentFirestoreRecord,
  EmergencyIncidentState,
  ResolveEmergencyInput,
} from '@src/models/interfaces/emergency.interface';
import { EMERGENCY_INCIDENT_STATES } from '@src/models/interfaces/emergency.interface';
import { EMERGENCY_COMMAND_OPERATIONS } from '@src/packages/emergencies/api/commands';
import { DateUtil } from '@src/packages/utils/dates';

import {
  chunkEmergencyQueryValues,
  executeEmergencyCommand,
  toFirestoreRecord,
} from './emergencyRepository.utils';

const compareIncidentCreatedAtDesc = (
  first: EmergencyIncidentFirestoreRecord,
  second: EmergencyIncidentFirestoreRecord,
): number =>
  (DateUtil.getTimestampMs(second.createdAt) ?? 0) - (DateUtil.getTimestampMs(first.createdAt) ?? 0);

export class EmergencyIncidentRepository {
  subscribeOne(
    organizationId: string,
    incidentId: string,
    onData: (record: EmergencyIncidentFirestoreRecord | null) => void,
    onError: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      doc(
        db,
        FSCollections.ORGANIZATIONS,
        organizationId,
        FSCollections.EMERGENCIES,
        incidentId,
      ),
      snapshot =>
        onData(
          snapshot.exists()
            ? ({ id: snapshot.id, ...snapshot.data() } as EmergencyIncidentFirestoreRecord)
            : null,
        ),
      onError,
    );
  }

  subscribeActive(
    organizationId: string,
    divisionIds: string[],
    organizationWide: boolean,
    onData: (records: EmergencyIncidentFirestoreRecord[]) => void,
    onError: (error: Error) => void,
  ): Unsubscribe {
    const incidentCollection = collection(
      db,
      FSCollections.ORGANIZATIONS,
      organizationId,
      FSCollections.EMERGENCIES,
    );
    const queries: Query[] = organizationWide
      ? [query(incidentCollection, where('state', '==', EMERGENCY_INCIDENT_STATES.ACTIVE))]
      : chunkEmergencyQueryValues(divisionIds).map(divisionChunk =>
          query(
            incidentCollection,
            where('divisionId', 'in', divisionChunk),
            where('state', '==', EMERGENCY_INCIDENT_STATES.ACTIVE),
          ),
        );
    if (!queries.length) {
      onData([]);
      return () => undefined;
    }
    const recordsByQuery = new Map<number, EmergencyIncidentFirestoreRecord[]>();
    const subscriptions = queries.map((incidentQuery, index) =>
      onSnapshot(
        incidentQuery,
        snapshot => {
          recordsByQuery.set(
            index,
            snapshot.docs.map(document =>
              toFirestoreRecord<EmergencyIncidentFirestoreRecord>(document),
            ),
          );
          onData(Array.from(recordsByQuery.values()).flat());
        },
        onError,
      ),
    );
    return () => subscriptions.forEach(unsubscribe => unsubscribe());
  }

  async list(
    organizationId: string,
    states?: EmergencyIncidentState[],
    divisionIds: string[] = [],
    organizationWide = false,
  ): Promise<EmergencyIncidentFirestoreRecord[]> {
    const incidentCollection = collection(
      db,
      FSCollections.ORGANIZATIONS,
      organizationId,
      FSCollections.EMERGENCIES,
    );
    // Firestore allows only one `in` per query. Prefer `state in` for org-wide
    // reads, and expand states into equality queries when divisionId already uses `in`.
    if (organizationWide) {
      const stateConstraints: QueryConstraint[] = [];
      if (states?.length === 1) stateConstraints.push(where('state', '==', states[0]));
      if (states && states.length > 1) stateConstraints.push(where('state', 'in', states));
      const snapshot = await getDocs(query(incidentCollection, ...stateConstraints));
      return snapshot.docs.map(docSnapshot =>
        toFirestoreRecord<EmergencyIncidentFirestoreRecord>(docSnapshot),
      );
    }
    if (!divisionIds.length) return [];

    const stateValues = states?.length ? states : [undefined];
    const snapshots = await Promise.all(
      stateValues.flatMap(state =>
        chunkEmergencyQueryValues(divisionIds).map(divisionChunk => {
          const constraints: QueryConstraint[] = [where('divisionId', 'in', divisionChunk)];
          if (state) constraints.push(where('state', '==', state));
          return getDocs(query(incidentCollection, ...constraints));
        }),
      ),
    );
    const byId = new Map<string, EmergencyIncidentFirestoreRecord>();
    snapshots.forEach(snapshot => {
      snapshot.docs.forEach(docSnapshot => {
        const record = toFirestoreRecord<EmergencyIncidentFirestoreRecord>(docSnapshot);
        byId.set(record.id, record);
      });
    });
    return Array.from(byId.values());
  }

  async get(
    organizationId: string,
    incidentId: string,
    divisionIds: string[],
    organizationWide: boolean,
  ): Promise<EmergencyIncidentFirestoreRecord | null> {
    const incidentCollection = collection(
      db,
      FSCollections.ORGANIZATIONS,
      organizationId,
      FSCollections.EMERGENCIES,
    );
    if (organizationWide) {
      const snapshot = await getDoc(doc(incidentCollection, incidentId));
      return snapshot.exists()
        ? ({ id: snapshot.id, ...snapshot.data() } as EmergencyIncidentFirestoreRecord)
        : null;
    }
    if (!divisionIds.length) return null;
    const snapshots = await Promise.all(
      chunkEmergencyQueryValues(divisionIds).map(divisionChunk =>
        getDocs(
          query(
            incidentCollection,
            where(documentId(), '==', incidentId),
            where('divisionId', 'in', divisionChunk),
          ),
        ),
      ),
    );
    const matchingDocument = snapshots.flatMap(snapshot => snapshot.docs)[0];
    return matchingDocument
      ? toFirestoreRecord<EmergencyIncidentFirestoreRecord>(matchingDocument)
      : null;
  }

  reassign(input: ReassignEmergencyResponderInput): Promise<EmergencyCommandResponse> {
    return executeEmergencyCommand(
      input.organizationId,
      input.divisionId,
      EMERGENCY_COMMAND_OPERATIONS.REASSIGN,
      {
        incidentId: input.incidentId,
        assignmentId: input.assignmentId,
        responderId: input.responderId,
      },
    );
  }

  assignPrimary(input: AssignPrimaryEmergencyResponderInput): Promise<EmergencyCommandResponse> {
    return executeEmergencyCommand(
      input.organizationId,
      input.divisionId,
      EMERGENCY_COMMAND_OPERATIONS.ASSIGN_PRIMARY,
      {
        incidentId: input.incidentId,
        responderId: input.responderId,
      },
    );
  }

  addNote(input: AddEmergencyNoteInput): Promise<EmergencyCommandResponse> {
    return executeEmergencyCommand(
      input.organizationId,
      input.divisionId,
      EMERGENCY_COMMAND_OPERATIONS.ADD_NOTE,
      { incidentId: input.incidentId, text: input.text },
    );
  }

  cancelFalseAlarm(input: CancelFalseAlarmInput): Promise<EmergencyCommandResponse> {
    return executeEmergencyCommand(
      input.organizationId,
      input.divisionId,
      EMERGENCY_COMMAND_OPERATIONS.CANCEL_FALSE_ALARM,
      { incidentId: input.incidentId, reason: input.reason },
    );
  }

  resolve(input: ResolveEmergencyInput): Promise<EmergencyCommandResponse> {
    return executeEmergencyCommand(
      input.organizationId,
      input.divisionId,
      EMERGENCY_COMMAND_OPERATIONS.RESOLVE,
      { incidentId: input.incidentId, reason: input.reason, outcome: input.outcome },
    );
  }

  async getCapabilities(
    organizationId: string,
    divisionId: string,
    incidentId: string,
  ): Promise<EmergencyCommandOperation[]> {
    const result = await executeEmergencyCommand(
      organizationId,
      divisionId,
      EMERGENCY_COMMAND_OPERATIONS.GET_CAPABILITIES,
      { incidentId },
    );
    return result.allowedCommands ?? [];
  }

  listActive(
    organizationId: string,
    divisionIds: string[],
    organizationWide: boolean,
  ): Promise<EmergencyIncidentFirestoreRecord[]> {
    return this.list(
      organizationId,
      [EMERGENCY_INCIDENT_STATES.ACTIVE],
      divisionIds,
      organizationWide,
    );
  }

  async listHistory(
    organizationId: string,
    divisionIds: string[],
    organizationWide: boolean,
    options: {
      fromMs?: number | null;
      toMs?: number | null;
      limitCount?: number;
    } = {},
  ): Promise<EmergencyIncidentFirestoreRecord[]> {
    const closedStates = [
      EMERGENCY_INCIDENT_STATES.CANCELED_FALSE_ALARM,
      EMERGENCY_INCIDENT_STATES.RESOLVED,
    ];
    const fromMs = options.fromMs ?? null;
    const toMs = options.toMs ?? null;
    const limitCount = options.limitCount ?? 500;
    const incidentCollection = collection(
      db,
      FSCollections.ORGANIZATIONS,
      organizationId,
      FSCollections.EMERGENCIES,
    );
    const buildRangeConstraints = (state: EmergencyIncidentState): QueryConstraint[] => [
      where('state', '==', state),
      ...(fromMs !== null ? [where('createdAt', '>=', Timestamp.fromMillis(fromMs))] : []),
      ...(toMs !== null ? [where('createdAt', '<=', Timestamp.fromMillis(toMs))] : []),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ];

    if (organizationWide) {
      const snapshots = await Promise.all(
        closedStates.map(state =>
          getDocs(query(incidentCollection, ...buildRangeConstraints(state))),
        ),
      );
      return snapshots
        .flatMap(snapshot =>
          snapshot.docs.map(docSnapshot =>
            toFirestoreRecord<EmergencyIncidentFirestoreRecord>(docSnapshot),
          ),
        )
        .sort(compareIncidentCreatedAtDesc)
        .slice(0, limitCount);
    }
    if (!divisionIds.length) return [];

    const snapshots = await Promise.all(
      closedStates.flatMap(state =>
        chunkEmergencyQueryValues(divisionIds).map(divisionChunk =>
          getDocs(
            query(
              incidentCollection,
              where('divisionId', 'in', divisionChunk),
              ...buildRangeConstraints(state),
            ),
          ),
        ),
      ),
    );
    const byId = new Map<string, EmergencyIncidentFirestoreRecord>();
    snapshots.forEach(snapshot => {
      snapshot.docs.forEach(docSnapshot => {
        const record = toFirestoreRecord<EmergencyIncidentFirestoreRecord>(docSnapshot);
        byId.set(record.id, record);
      });
    });
    return Array.from(byId.values()).sort(compareIncidentCreatedAtDesc).slice(0, limitCount);
  }
}
